import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { collection, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { chatService } from '../services/chatService';
import { useAuth } from '../contexts/AuthContext';
import { firebaseApp, firebaseAuth } from '../firebase';

const ENABLE_SOCKET = process.env.REACT_APP_ENABLE_SOCKET === 'true';
const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL?.replace('/api', '') || '';
// Firebase-only: par défaut on utilise Firestore pour le chat.
const USE_FIRESTORE_CHAT = process.env.REACT_APP_USE_FIRESTORE_CHAT !== 'false';

function firestoreMessageToUi(docSnap) {
  const d = docSnap.data();
  const created = d.createdAt?.toDate?.() ?? new Date();
  const preview = d.senderPreview || {};
  return {
    _id: docSnap.id,
    content: d.content,
    type: d.type || 'text',
    read: Boolean(d.read),
    createdAt: created.toISOString(),
    senderId: {
      _id: d.senderUid || d.senderMongoId,
      firstName: preview.firstName,
      lastName: preview.lastName,
      email: preview.email,
      role: preview.role || d.senderRole || 'client',
    },
  };
}

const Chat = ({ conversationId, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [typing, setTyping] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const pollingRef = useRef(null);

  const useFirestoreMessages =
    USE_FIRESTORE_CHAT && Boolean(firebaseApp) && Boolean(firebaseUser);

  useEffect(() => {
    if (!USE_FIRESTORE_CHAT || !firebaseAuth) return undefined;
    const unsub = onAuthStateChanged(firebaseAuth, setFirebaseUser);
    return () => unsub();
  }, []);

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const response = await chatService.getMessages(conversationId);
      if (response.success) {
        setMessages(response.messages || []);
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || !useFirestoreMessages || !firebaseApp) return undefined;

    setLoading(true);
    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => firestoreMessageToUi(d));
        setMessages(next);
        setLoading(false);
        requestAnimationFrame(() =>
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        );
      },
      (err) => {
        console.error('Firestore chat:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [conversationId, useFirestoreMessages, firebaseUser?.uid]);

  useEffect(() => {
    if (!conversationId || useFirestoreMessages) return undefined;

    let cancelled = false;
    let socketInstance = null;

    (async () => {
      await loadMessages();
      if (cancelled) return;

      if (ENABLE_SOCKET) {
        let token = null;
        if (firebaseAuth?.currentUser) {
          try {
            token = await firebaseAuth.currentUser.getIdToken();
          } catch (e) {
            console.warn('Socket.IO token Firebase:', e);
          }
        }
        socketInstance = io(SOCKET_BASE_URL, {
          auth: { token },
          transports: ['websocket', 'polling'],
        });

        socketInstance.on('connect', () => {
          console.log('✅ Connecté au serveur Socket.IO');
          socketInstance.emit('join:conversation', conversationId);
        });

        socketInstance.on('message:new', (data) => {
          if (data.conversationId === conversationId) {
            setMessages((prev) => [...prev, data.message]);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        });

        socketInstance.on('message:read', () => {
          loadMessages();
        });

        socketInstance.on('typing:start', (data) => {
          const selfId = user?.id || user?.uid;
          if (data.conversationId === conversationId && data.userId !== selfId) {
            setIsTyping(true);
          }
        });

        socketInstance.on('typing:stop', (data) => {
          const selfId = user?.id || user?.uid;
          if (data.conversationId === conversationId && data.userId !== selfId) {
            setIsTyping(false);
          }
        });

        socketInstance.on('connect_error', (error) => {
          console.error('Erreur de connexion Socket.IO:', error);
        });

        if (cancelled) {
          socketInstance.disconnect();
          return;
        }
        setSocket(socketInstance);
      } else {
        pollingRef.current = setInterval(() => {
          loadMessages();
        }, 5000);
        setSocket(null);
      }
    })();

    return () => {
      cancelled = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (ENABLE_SOCKET) {
        socketInstance?.disconnect();
        setSocket(null);
      }
    };
  }, [conversationId, user.id, useFirestoreMessages, loadMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    setNewMessage('');

    const sendHttp = async () => {
      const response = await chatService.sendMessage(conversationId, messageContent, 'text');
      if (!response.success) return;
      if (!useFirestoreMessages) {
        await loadMessages();
      }
    };

    if (useFirestoreMessages || !ENABLE_SOCKET || !socket) {
      await sendHttp();
      if (typing && socket) {
        socket.emit('typing:stop', { conversationId });
        setTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
      return;
    }

    socket.emit('message:send', {
      conversationId,
      content: messageContent,
      type: 'text',
    });

    if (typing) {
      socket.emit('typing:stop', { conversationId });
      setTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);

    if (useFirestoreMessages || !ENABLE_SOCKET || !socket) {
      return;
    }

    if (!typing && socket) {
      socket.emit('typing:start', { conversationId });
      setTyping(true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (socket) {
        socket.emit('typing:stop', { conversationId });
        setTyping(false);
      }
    }, 1000);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#fff',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    }}>
      {/* En-tête du chat */}
      <div style={{
        padding: '15px 20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
          💬 Chat Support
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Zone des messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        backgroundColor: '#f5f5f5',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Chargement des messages...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Aucun message. Commencez la conversation !
          </div>
        ) : (
          <>
            {messages.map((message) => {
              const senderId = message.senderId._id || message.senderId?.id || message.senderId;
              const userId = user.id || user.uid || user._id;
              const isOwn = senderId === userId || senderId?.toString() === userId?.toString();
              return (
                <div
                  key={message._id}
                  style={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    marginBottom: '15px',
                  }}
                >
                  <div style={{
                    maxWidth: '70%',
                    padding: '10px 15px',
                    borderRadius: '18px',
                    backgroundColor: isOwn ? '#FF0000' : '#e0e0e0',
                    color: isOwn ? '#fff' : '#333',
                  }}>
                    {!isOwn && (
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '5px',
                        color: isOwn ? '#fff' : '#666',
                      }}>
                        {message.senderId?.firstName || message.senderId?.firstName || ''} {message.senderId?.lastName || message.senderId?.lastName || ''}
                      </div>
                    )}
                    <div style={{ fontSize: '14px', wordWrap: 'break-word' }}>
                      {message.content}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      marginTop: '5px',
                      opacity: 0.7,
                      textAlign: 'right',
                    }}>
                      {formatTime(message.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
            {isTyping && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: '15px',
              }}>
                <div style={{
                  padding: '10px 15px',
                  borderRadius: '18px',
                  backgroundColor: '#e0e0e0',
                  color: '#666',
                  fontSize: '14px',
                  fontStyle: 'italic',
                }}>
                  En train d'écrire...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Zone de saisie */}
      <form onSubmit={handleSendMessage} style={{
        padding: '15px 20px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#fff',
      }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={newMessage}
            onChange={handleTyping}
            placeholder="Tapez votre message..."
            style={{
              flex: 1,
              padding: '10px 15px',
              border: '1px solid #ddd',
              borderRadius: '20px',
              fontSize: '14px',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF0000',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
              opacity: newMessage.trim() ? 1 : 0.5,
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Envoyer
          </button>
        </div>
      </form>
    </div>
  );
};

export default Chat;
